import PropTypes from 'prop-types';
import React, { useContext, useState, useRef } from 'react';
import { Configure } from 'react-instantsearch-dom';

// Components
import SearchBox from '../../../../../algolia-search/js/components/elements/SearchBox';
import PagingToolbar from '../../../../../algolia-search/js/components/sections/PagingToolbar';
import SearchOptionsDesktop from '../../../../../algolia-search/js/components/sections/SearchOptionsDesktop';
import SearchOptionsMobile from '../../../../../algolia-search/js/components/sections/SearchOptionsMobile';
import SearchContainer from '@search/SearchContainer';

// Functionality
import {
    PageSizeContext,
    SearchStateContext,
    SortByContext,
} from '../../../../../algolia-search/js/context/searchContext';

import { searchStateToUrl, urlToSearchState } from '../../../../../algolia-search/js/helpers/search';
import { ViewTypeContext } from '@context/viewTypeContext';
import { useScreenMdDown, useScreenMinLg } from '@hooks/useResponsive';

const DEBOUNCE_TIME = 400;

const BasicSearch = ({
    children,
    aroundLatLngViaIP = false,
    displayMap = false,
    displayPagingToolbar = true,
    displaySearchBox = true,
    displaySearchOptions = true,
    filters,
    hitsPerPage,
    index,
    initialSearchState,
    maxWidth = '1200px',
    optionalFilters = [],
    updateSearchStateUrl = false,
    RefinementComponent,
    AboveSearchBoxComponent
}) => {
    const [analyticsTags] = useState([]);
    const [searchState, setSearchState] = useState(initialSearchState || urlToSearchState(window.location));
    const [debouncedSetState, setDebouncedSetState] = useState(null);
    const [sortBy] = useContext(SortByContext);
    const [pageSize] = useContext(PageSizeContext);
    const [viewType] = useContext(ViewTypeContext);

    const containerDesktop = useRef(null);
    const containerMobile = useRef(null);

    const onSearchStateChange = ({
        searchTypeIndex,
        sortByQueryParam,
        pageSizeHitsPerPage,
        viewType: updatedViewType,
        searchState: updatedSearchState,
    }) => {
        if (updateSearchStateUrl) {
            clearTimeout(debouncedSetState);
            setDebouncedSetState(
                setTimeout(() => {
                    const url = searchStateToUrl({
                        searchType: searchTypeIndex,
                        sortBy: sortByQueryParam,
                        pageSize: pageSizeHitsPerPage,
                        viewType: updatedViewType,
                        searchState: updatedSearchState,
                    });

                    window.history.pushState('', '', url);

                    if (window.ga) {
                        window.ga('send', 'pageView', url);
                    }
                }, DEBOUNCE_TIME)
            );
        }

        setSearchState(updatedSearchState);
    };

    const isTabletDown = useScreenMdDown();
    const isDesktop = useScreenMinLg();

    const renderDesktop = () => (
        <div className="container-fluid px-0" style={{ maxWidth }}>
            <div className="d-flex" ref={containerDesktop}>
                <div className="w-100">
                    <div className={`mb-3 ${displaySearchOptions ? '' : 'd-none'}`}>
                        <SearchOptionsDesktop
                            displayMap={displayMap}
                            pageSize={pageSize}
                            onPageSizeChange={selectedPageSize => {
                                onSearchStateChange({
                                    searchTypeIndex: index,
                                    sortByQueryParam: sortBy?.queryParam,
                                    pageSizeHitsPerPage: selectedPageSize.hitsPerPage,
                                    viewType,
                                    searchState,
                                });
                            }}
                            onSortByChange={selectedSortBy => {
                                onSearchStateChange({
                                    searchTypeIndex: index,
                                    sortByQueryParam: selectedSortBy?.queryParam,
                                    pageSizeHitsPerPage: pageSize.hitsPerPage,
                                    viewType,
                                    searchState,
                                });
                            }}
                            onViewTypeChange={selectedViewType => {
                                onSearchStateChange({
                                    searchTypeIndex: index,
                                    sortByQueryParam: sortBy?.queryParam,
                                    pageSizeHitsPerPage: pageSize.hitsPerPage,
                                    viewType: selectedViewType,
                                    searchState,
                                });
                            }}
                        />
                    </div>
                    <div>{children}</div>
                    {displayPagingToolbar && (
                        <div className="mt-1rem">
                            <PagingToolbar scrollToRef={containerDesktop} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderMobile = () => (
        <>
            <div ref={containerMobile}>
                {displaySearchOptions && (
                    <div className="mb-3">
                        <SearchOptionsMobile
                            onPageSizeChange={selectedPageSize => {
                                onSearchStateChange({
                                    searchTypeIndex: index,
                                    sortByQueryParam: sortBy?.queryParam,
                                    pageSizeHitsPerPage: selectedPageSize.hitsPerPage,
                                    viewType,
                                    searchState,
                                });
                            }}
                            onSortByChange={selectedSortBy => {
                                onSearchStateChange({
                                    searchTypeIndex: index,
                                    sortByQueryParam: selectedSortBy?.queryParam,
                                    pageSizeHitsPerPage: pageSize.hitsPerPage,
                                    viewType,
                                    searchState,
                                });
                            }}
                        />
                    </div>
                )}
                <div className="mb-3">{children}</div>

                {displayPagingToolbar && (
                    <div className="mt-1rem">
                        <PagingToolbar scrollToRef={containerMobile} />
                    </div>
                )}
            </div>
        </>
    );

    return (
        <SearchContainer
            key={index}
            index={index}
            searchState={searchState}
            onSearchStateChange={updatedSearchState =>
                onSearchStateChange({
                    searchTypeIndex: index,
                    sortByQueryParam: sortBy?.queryParam,
                    pageSizeHitsPerPage: pageSize.hitsPerPage,
                    viewType,
                    searchState: updatedSearchState,
                })
            }
        >
            <Configure
                analyticsTags={analyticsTags}
                clickAnalytics
                hitsPerPage={hitsPerPage}
                filters={filters}
                maxValuesPerFacet={100}
                optionalFilters={optionalFilters}
                aroundLatLngViaIP={aroundLatLngViaIP}
            />

            <SearchStateContext.Provider value={[searchState]}>
                <>
                    {AboveSearchBoxComponent && <AboveSearchBoxComponent />}
                    {displaySearchBox && (
                        <div className="mb-3">
                            <SearchBox debounceTime={DEBOUNCE_TIME} placeholder="Search for..." />
                        </div>
                    )}

                    {RefinementComponent && (
                        <div className="mb-0.625rem">
                            <RefinementComponent />
                        </div>
                    )}

                    {isDesktop && renderDesktop()}
                    {isTabletDown && renderMobile()}
                </>
            </SearchStateContext.Provider>
        </SearchContainer>
    );
};

BasicSearch.propTypes = {
    children: PropTypes.node.isRequired,
    aroundLatLngViaIP: PropTypes.bool,
    displayMap: PropTypes.bool,
    displayPagingToolbar: PropTypes.bool,
    displaySearchBox: PropTypes.bool,
    displaySearchOptions: PropTypes.bool,
    filters: PropTypes.string,
    hitsPerPage: PropTypes.number,
    index: PropTypes.string.isRequired,
    initialSearchState: PropTypes.object,
    maxWidth: PropTypes.string,
    optionalFilters: PropTypes.array,
    updateSearchStateUrl: PropTypes.bool,
    RefinementComponent: PropTypes.func,
};

export default BasicSearch;
